import { Button, Card, Flex, Text } from '@servicetitan/anvil2';
import { openWebUrl } from '@servicetitan/financing-utils-shared';
import { SubmitApplicationDecision } from '@servicetitan/financing-api-client';
import { SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import {
    useProviderConfig,
    SetupDataStore,
    WorkflowStore,
    WorkflowButtonsStore,
    useTurnsIntegration,
} from '@servicetitan/financing-workflow-shared';
import { DecisionDataStore } from '../stores/DecisionDataStore';
import {
    ApplicationDataStore,
    DisclosureDataStore,
} from '@servicetitan/financing-application-shared';

export const SecondLookFinancingOption = () => {
    const { setupData, setSetupData } = SetupDataStore.useStore();
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { decisionData, setDecisionData } = DecisionDataStore.useStore();
    const { setSelectedPlan } = SelectedPlanStore.useStore();
    const { applicationData, setApplicationData } = ApplicationDataStore.useStore();
    const { setAgreedToDisclosures } = DisclosureDataStore.useStore();
    const providerConfig = useProviderConfig();
    const { CloseWithoutConfirmingButton } = WorkflowButtonsStore.useStore();
    const turnsIntegration = useTurnsIntegration();

    if (!turnsIntegration.isEnabled) {
        return null;
    }

    // Don't show second look option if we're already in a second look flow
    if (setupData?.isInSecondLook) {
        return null;
    }

    const isSecondaryLender = providerConfig.providerCategory === 'second-look';

    // Don't show manual second look option when using first-look provider with waterfall enabled
    // (providerFlagEnabled means automatic waterfall to Turns on decline for first-look providers)
    // BUT still show it for second-look providers (different use case)
    if (!isSecondaryLender && turnsIntegration.providerFlagEnabled) {
        return null;
    }

    // Don't show anything for secondary lender without redirect URL
    if (isSecondaryLender && !decisionData?.declineRedirectUrl) {
        return null;
    }

    const setJobDetailsAddress = () => {
        const updatedApplicationData = { ...applicationData };

        // Ensure applicant structure exists
        if (!updatedApplicationData.applicant) {
            updatedApplicationData.applicant = {};
        }
        if (!updatedApplicationData.applicant.primaryApplicant) {
            updatedApplicationData.applicant.primaryApplicant = {};
        }
        if (!updatedApplicationData.applicant.primaryApplicant.jobDetails) {
            updatedApplicationData.applicant.primaryApplicant.jobDetails = {};
        }

        // Set jobDetails.address based on conditions
        if (providerConfig.defaultWorkflowStage === 'external-application') {
            updatedApplicationData.applicant.primaryApplicant.email =
                updatedApplicationData.externalApplication?.email;
            updatedApplicationData.applicant.primaryApplicant.primaryPhone =
                updatedApplicationData.externalApplication?.primaryPhone;
            // Use external application address if available
            if (updatedApplicationData.externalApplication?.address) {
                updatedApplicationData.applicant.primaryApplicant.jobDetails.address =
                    updatedApplicationData.externalApplication.address;
            }
        } else if (updatedApplicationData.mailingAddress?.primaryApplicant?.address) {
            // Use applicant address (from mailingAddress in the data structure)
            updatedApplicationData.applicant.primaryApplicant.jobDetails.address =
                updatedApplicationData.mailingAddress.primaryApplicant.address;
        }

        setApplicationData(updatedApplicationData);
    };

    const handleButtonClick = () => {
        if (isSecondaryLender && decisionData?.declineRedirectUrl) {
            openWebUrl(decisionData.declineRedirectUrl, setupData?.useInAppBrowser);
            setWorkflowStage('reset');
        } else if (setupData) {
            setJobDetailsAddress();
            setAgreedToDisclosures(false);
            setSetupData({
                ...setupData,
                provider: 'Turns',
                isInSecondLook: true,
            });
            setWorkflowStage('second-look-applicant-information');
            if (decisionData) {
                setDecisionData({
                    ...decisionData,
                    decision: SubmitApplicationDecision.NotSet,
                });
            }
            setSelectedPlan(null);
        }
    };

    return (
        <Card
            data-testid="second-look-financing-option"
            flexDirection="column"
            style={{
                maxWidth: '845px',
                width: '100%',
            }}
        >
            <Flex gap="3" direction="column" justifyContent="center">
                <Text variant="headline" el="h2" size="small" style={{ textAlign: 'center' }}>
                    {isSecondaryLender
                        ? 'Explore more financing options'
                        : 'Turns Financing provides second look financing to declined applicants'}
                </Text>
                <Text variant="body" color="secondary" style={{ textAlign: 'center' }}>
                    {isSecondaryLender
                        ? 'Turns Financing may be able to provide you with alternative options. Additional information may be required to qualify.'
                        : 'Prequalify now to receive a decision with no impact to your credit score.'}
                </Text>
                <Button
                    type="button"
                    appearance="primary"
                    style={{ width: '100%' }}
                    onClick={handleButtonClick}
                >
                    {isSecondaryLender ? 'Explore Options' : 'Prequalify Now'}
                </Button>
                {!isSecondaryLender && (
                    <CloseWithoutConfirmingButton style={{ width: '100%' }}>
                        Return to Estimates
                    </CloseWithoutConfirmingButton>
                )}
            </Flex>
        </Card>
    );
};
